import { Link, useLocation, useParams } from "react-router-dom";
import { FormEvent, useEffect, useMemo, useState } from "react";
import { api, type Column } from "@/lib/api";
import { ROUTES } from "@/routes";
import { EndpointRateLimitPanel } from "@/components/EndpointRateLimitPanel";
import { ErrorBox, PageHeader, Spinner } from "@/components/ui";

export function TableViewerPage() {
  const { id = "", table = "" } = useParams<{ id: string; table: string }>();
  const location = useLocation();
  const databaseTablesPath = ROUTES.databaseTables(id);
  const isDatabaseTablesRoute =
    location.pathname === databaseTablesPath ||
    location.pathname.startsWith(`${databaseTablesPath}/`);
  const tableRoot = isDatabaseTablesRoute
    ? databaseTablesPath
    : ROUTES.tables(id);
  const [columns, setColumns] = useState<Column[]>([]);
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [insertJson, setInsertJson] = useState("{}");
  const [filterCol, setFilterCol] = useState("");
  const [filterVal, setFilterVal] = useState("");

  const pk = useMemo(
    () => columns.find((c) => c.is_primary_key)?.name,
    [columns],
  );

  async function load(qs = "") {
    setLoading(true);
    setError(null);
    try {
      const [desc, data] = await Promise.all([
        api.describeTable(id, table),
        api.dataSelect(id, table, qs || "limit=50"),
      ]);
      setColumns(desc.data.columns);
      setRows(data.data);
      if (!filterCol && desc.data.columns[0]) {
        setFilterCol(desc.data.columns[0].name);
      }
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!id || !table) return;
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, table]);

  async function onInsert(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      const row = JSON.parse(insertJson) as Record<string, unknown>;
      await api.dataInsert(id, table, row);
      setInsertJson("{}");
      await load();
    } catch (err) {
      setError(err);
    }
  }

  async function onFilter(e: FormEvent) {
    e.preventDefault();
    if (!filterCol || filterVal === "") {
      await load("limit=50");
      return;
    }
    const qs = `limit=50&${encodeURIComponent(filterCol)}=eq.${encodeURIComponent(filterVal)}`;
    await load(qs);
  }

  async function onDelete(row: Record<string, unknown>) {
    if (!pk) {
      setError(new Error("No primary key — cannot delete from UI"));
      return;
    }
    const val = row[pk];
    if (val == null) return;
    if (!confirm(`Delete row where ${pk}=${String(val)}?`)) return;
    try {
      await api.dataDelete(id, table, pk, String(val));
      await load();
    } catch (err) {
      setError(err);
    }
  }

  return (
    <div>
      <PageHeader
        eyebrow="Table"
        title={table}
        description="Browse rows with filter, insert, and delete. Configure advanced endpoint rate limits below."
        actions={
          <Link className="btn-secondary" to={tableRoot}>
            All tables
          </Link>
        }
      />
      {error ? (
        <div className="mb-4">
          <ErrorBox error={error} />
        </div>
      ) : null}

      <div className="mb-4 grid gap-4 lg:grid-cols-3">
        <div className="card p-4 lg:col-span-1">
          <h2 className="mb-3 font-sans text-sm font-semibold text-text-primary">
            Columns
          </h2>
          <ul className="space-y-2 text-xs">
            {columns.map((c) => (
              <li
                key={c.name}
                className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 border-b border-border-standard/60 pb-2 last:border-0 last:pb-0"
              >
                <span className="font-mono text-text-primary">{c.name}</span>
                <span className="text-text-disabled">{c.udt_name}</span>
                {c.is_primary_key ? (
                  <span className="rounded bg-secondary-fixed px-1.5 py-0.5 font-mono text-[10px] font-semibold text-secondary">
                    PK
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
        <form
          onSubmit={onFilter}
          className="card flex flex-wrap items-end gap-2 p-4 lg:col-span-2"
        >
          <div className="min-w-[8rem] flex-1">
            <label className="label">Filter column</label>
            <select
              className="input"
              value={filterCol}
              onChange={(e) => setFilterCol(e.target.value)}
            >
              {columns.map((c) => (
                <option key={c.name} value={c.name}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div className="min-w-[8rem] flex-1">
            <label className="label">Equals</label>
            <input
              className="input"
              value={filterVal}
              onChange={(e) => setFilterVal(e.target.value)}
              placeholder="value"
            />
          </div>
          <button className="btn-secondary" type="submit">
            Apply
          </button>
          <button
            className="btn-ghost"
            type="button"
            onClick={() => {
              setFilterVal("");
              void load("limit=50");
            }}
          >
            Clear
          </button>
        </form>
      </div>

      {loading ? (
        <Spinner label="Loading rows…" />
      ) : (
        <div className="table-wrap mb-6">
          <table className="data">
            <thead>
              <tr>
                {columns.map((c) => (
                  <th key={c.name}>{c.name}</th>
                ))}
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i}>
                  {columns.map((c) => (
                    <td key={c.name} title={String(row[c.name] ?? "")}>
                      {formatCell(row[c.name])}
                    </td>
                  ))}
                  <td>
                    <button
                      className="btn-danger !px-2 !py-1 text-xs"
                      type="button"
                      onClick={() => void onDelete(row)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length + 1} className="!text-text-muted">
                    No rows match this view. Insert a row below or clear the
                    filter.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      )}

      <form onSubmit={onInsert} className="card mb-6 space-y-3 p-5">
        <h2 className="font-sans text-sm font-semibold text-text-primary">
          Insert row (JSON)
        </h2>
        <textarea
          className="input min-h-[100px] font-mono text-xs"
          value={insertJson}
          onChange={(e) => setInsertJson(e.target.value)}
        />
        <button className="btn-primary" type="submit">
          Insert
        </button>
      </form>

      {id && table ? (
        <EndpointRateLimitPanel
          projectId={id}
          table={table}
          onError={(e) => setError(e)}
        />
      ) : null}
    </div>
  );
}

function formatCell(v: unknown): string {
  if (v == null) return "null";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}
